// MARCA | 18/05/2026 | Delivery countdown custom element — counts down to next-day delivery cutoff.
// MARCA | 18/05/2026 | Added: custom end date/time override mode, optional days display, day-only date format.
// MARCA | 24/08/2026 | Product before-label swaps "Today" for "in X days" based on remaining sale days.
// Reads per-day cutoff hours (Sun–Sat) and blocked dates from data attributes set by global theme settings.
if (!customElements.get("delivery-countdown")) {
  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.daysEl = this.querySelector(".js-delivery-days");
      this.hoursEl = this.querySelector(".js-delivery-hours");
      this.minsEl = this.querySelector(".js-delivery-mins");
      this.secsEl = this.querySelector(".js-delivery-secs");
      this.dateEl = this.querySelector(".js-delivery-date");
      this.beforeEl = this.querySelector(".delivery-countdown__label--before");
      this.dayOnly = this.dataset.dayOnly === "true";
      this.dynamicBefore = this.dataset.dynamicBefore === "true";
      this.labelToday = this.dataset.labelToday || (this.beforeEl ? this.beforeEl.textContent : "");

      const cutoffStr = this.dataset.cutoffs || "";
      this.cutoffs = cutoffStr.split(",").map((v) => parseInt(v, 10) || 0);

      this.blockedDates = (this.dataset.blockedDates || "")
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);

      this.fixedEndDate = this.parseFixedEnd();

      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    parseFixedEnd() {
      const dateStr = this.dataset.endDate;
      if (!dateStr) return null;
      const timeStr = this.dataset.endTime || "23:59";
      const dt = new Date(dateStr + "T" + timeStr + ":00");
      return isNaN(dt.getTime()) ? null : dt;
    }

    isNonDeliveryDay(date) {
      const dayIndex = date.getDay();
      if (this.cutoffs[dayIndex] === 0) return true;

      const key =
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");
      return this.blockedDates.includes(key);
    }

    getCutoffHour(date) {
      return this.cutoffs[date.getDay()] || 16;
    }

    getNextCutoffTime() {
      const now = new Date();
      let candidate = new Date(now);

      while (this.isNonDeliveryDay(candidate)) {
        candidate.setDate(candidate.getDate() + 1);
        candidate.setHours(0, 0, 0, 0);
      }

      const cutoffHour = this.getCutoffHour(candidate);
      let cutoff = new Date(candidate);
      cutoff.setHours(cutoffHour, 0, 0, 0);

      if (now.getTime() > cutoff.getTime()) {
        let nextDay = new Date(candidate);
        do {
          nextDay.setDate(nextDay.getDate() + 1);
          nextDay.setHours(0, 0, 0, 0);
        } while (this.isNonDeliveryDay(nextDay));

        cutoff = new Date(nextDay);
        cutoff.setHours(this.getCutoffHour(nextDay), 0, 0, 0);
      }

      return cutoff;
    }

    getNextDeliveryDate(cutoff) {
      const delivery = new Date(cutoff);
      do {
        delivery.setDate(delivery.getDate() + 1);
      } while (this.isNonDeliveryDay(delivery));
      return delivery;
    }

    // MARCA | 24/08/2026 | PDP sale label: "Hurry! Ends Today" when 0 days remain, otherwise "Hurry! Ends in X days".
    updateBeforeLabel(days) {
      if (!this.dynamicBefore || !this.beforeEl || !this.labelToday) return;

      if (days <= 0) {
        this.beforeEl.textContent = this.labelToday;
        return;
      }

      const unit = days === 1 ? "day" : "days";
      const inDays = "in " + days + " " + unit;
      if (/today/i.test(this.labelToday)) {
        this.beforeEl.textContent = this.labelToday.replace(/today/i, inDays);
      } else {
        this.beforeEl.textContent = this.labelToday + " " + inDays;
      }
    }

    formatDeliveryDate(date) {
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      if (this.dayOnly) return days[date.getDay()];
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return days[date.getDay()] + " " + date.getDate() + " " + months[date.getMonth()];
    }

    updateCountdown() {
      const now = new Date();
      const target = this.fixedEndDate || this.getNextCutoffTime();
      const diff = target.getTime() - now.getTime();

      if (diff <= 0) {
        if (this.daysEl) this.daysEl.textContent = "00";
        this.hoursEl.textContent = "00";
        this.minsEl.textContent = "00";
        if (this.secsEl) this.secsEl.textContent = "00";
        this.updateBeforeLabel(0);
        this.setAttribute("loaded", "");
        return;
      }

      const totalHours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(totalHours / 24);
      const hours = this.daysEl ? totalHours % 24 : totalHours;
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      if (this.daysEl) this.daysEl.textContent = days.toString().padStart(2, "0");
      this.hoursEl.textContent = hours.toString().padStart(2, "0");
      this.minsEl.textContent = minutes.toString().padStart(2, "0");
      if (this.secsEl) this.secsEl.textContent = seconds.toString().padStart(2, "0");

      this.updateBeforeLabel(days);

      if (this.dateEl) {
        if (this.fixedEndDate) {
          this.dateEl.textContent = this.formatDeliveryDate(this.fixedEndDate);
        } else {
          this.dateEl.textContent = this.formatDeliveryDate(
            this.getNextDeliveryDate(target)
          );
        }
      }

      this.setAttribute("loaded", "");
    }
  }

  customElements.define("delivery-countdown", DeliveryCountdown);
}
